import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { CellContext } from '@tanstack/react-table'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import {
  PiDownloadSimple,
  PiEye,
  PiPencilSimple,
  PiSwap,
  PiTag,
  PiTrash,
} from 'react-icons/pi'
import { DataTable, DataTableProvider, useDataTableContext } from '../DataTable'
import { IColumnVisibility } from '../DataTable/types'
import { DataTableContainer } from '../DataTable/UI'
import {
  getDocuments,
  bulkDownloadDocuments,
  getBulkDocumentDownloadStatus,
} from '../../services/documentsApi'
import { getTableQueryKey } from '../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../DataTable/DataTableSorting/DataTableSortingStore'
import { ActionsDropdown } from '../Buttons/ActionsDropdown'
import { TaglistCell } from '../FbUI/StagePill'
import { ColumnsStoreProvider } from '../../stores/ColumnsStore/ColumnsStoreProvider'
import { filterStoreRepo, useFilterParams } from '../Filters/FilterStore'
import { FilterIdentifier } from '../../models/saved_view'
import { TableSearch } from '../Filters/TableSearch'
import { FilterChips } from '../Filters/components/FilterChips/FilterChips'
import * as S from '../Tables/CommonTable.styles'
import { DocumentsFilterset } from './DocumentsFilterset'
import { ColumnSelectorRecipient } from '../Modals/ColumnModal/ColumnModal'
import { Document } from '../../types/documents'
import { cn } from '../UI/cn'
import { TruncatedLinkCell } from '../Tables/CommonTableCells/TruncatedLinkCell'
import { EditDocumentModal } from './EditDocumentModal'
import { DeleteDocumentConfirmation } from './DeleteDocumentConfirmation'
import { ReplaceDocumentModal } from './ReplaceDocumentModal'
import TableButtonAction from '../Buttons/TableButtons/TableButtonAction'
import { useTableLinkVariant } from '../Tables/CommonTableCells/TableLinkStyleContext'
import { useDownload } from '../../features/download-widget'
import { DocumentLinkBrandCombobox } from './DocumentLinkBrandCombobox'
import { useFeatureFlagService } from '../../utils/useFeatureFlagService'

const PAGE_SIZE = 100

type TabType = 'all' | 'company' | 'product'

interface DocumentsTableProps {
  tableKey: string
  filterIdentifier: FilterIdentifier
  tabType: TabType
  forChainProxyId?: number
  forDoorProxyId?: number
  forVariantId?: number
  forDealId?: number
  /** Filter to only show non-image documents */
  excludeImages?: boolean
  disableUrlPagination?: boolean
  disableExportButton?: boolean
  /** Hide the column selector button */
  hideColumnSelector?: boolean
  /** Hide the filter button and filter chips */
  hideFilterButton?: boolean
  /** Custom placeholder text for search input */
  searchPlaceholder?: string
}

function DocumentsTableComponent({
  tableKey,
  filterIdentifier,
  tabType,
  forChainProxyId,
  forDoorProxyId,
  forVariantId,
  forDealId,
  excludeImages,
  hideColumnSelector = false,
  hideFilterButton = false,
  searchPlaceholder = 'Search by name or company...',
}: DocumentsTableProps) {
  const { enableBrandManagement } = useFeatureFlagService()
  const queryClient = useQueryClient()
  const linkVariant = useTableLinkVariant()
  const documentsFilterStore = filterStoreRepo.getStore(filterIdentifier)
  const { page, setPage } = documentsFilterStore()
  const filterParams = useFilterParams(documentsFilterStore)
  const sortingStore = dataTableSortingStoreRepo.getStore(tableKey)
  const { sorting, setSorting } = sortingStore()

  // Row selection state
  const {
    state: { rowSelection, totalSelectedRows },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  // Bulk download state
  const { startDownload, state: downloadState } = useDownload()

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [replaceModalOpen, setReplaceModalOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  )

  // Set default sort to created descending on initial load
  useEffect(() => {
    if (!sorting || sorting.length === 0) {
      setSorting([{ id: 'created', desc: true }])
    }
  }, [])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['ordering'] = sorting[0].desc
        ? '-' + sorting[0]?.id
        : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(() => {
    let params: Record<string, any> = {
      ...filterParams,
      ...sortParams,
      tab: tabType,
    }
    if (forChainProxyId) {
      params = {
        ...params,
        contact_company: forChainProxyId,
      }
    }
    if (forDoorProxyId) {
      params = {
        ...params,
        door_proxy_id: forDoorProxyId,
      }
    }
    if (forVariantId) {
      params = {
        ...params,
        variant: [String(forVariantId)],
      }
    }
    if (forDealId) {
      params = {
        ...params,
        deal: forDealId,
      }
    }
    if (excludeImages) {
      params = {
        ...params,
        is_image: false,
      }
    }
    return params
  }, [
    filterParams,
    sortParams,
    tabType,
    forChainProxyId,
    forDoorProxyId,
    forVariantId,
    forDealId,
    excludeImages,
  ])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey,
    filterParams: filterAndSortParams,
    page,
  })

  const { isFetching, data } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async () => {
      clearSelectedRows()
      return await getDocuments({
        ...filterAndSortParams,
        page,
        page_size: PAGE_SIZE,
      })
    },
  })

  // Update total rows in backend for selection tracking
  useEffect(() => {
    if (data?.count != null) {
      setTotalRowsInBackend(data.count)
    }
  }, [data?.count, setTotalRowsInBackend])

  // Get selected document IDs from row selection
  const selectedDocumentIds = useMemo(() => {
    if (!rowSelection || !data?.results) return []
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((index) => data.results[parseInt(index)]?.id)
      .filter(Boolean) as number[]
  }, [rowSelection, data?.results])

  // Bulk download documents handler
  const handleBulkDownloadDocuments = useCallback(() => {
    if (!selectedDocumentIds.length) {
      toast.warning('Please select at least one document')
      return
    }

    const items = selectedDocumentIds.map((docId) => {
      const doc = data?.results?.find((d) => d.id === docId)
      return { id: docId, name: doc?.name || `Document ${docId}` }
    })

    startDownload({
      items,
      initiate: async (signal) => {
        const response = await bulkDownloadDocuments(
          selectedDocumentIds,
          signal
        )
        if ('blob' in response)
          return {
            type: 'blob',
            blob: response.blob,
            filename: response.filename,
          }
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getBulkDocumentDownloadStatus,
      label: 'documents',
    })
  }, [selectedDocumentIds, data?.results, startDownload])

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: () => null,
        accessorKey: 'name',
        cell: ({ getValue, row }: CellContext<Document, unknown>) => {
          const name = getValue() as string
          const fileUrl = row.original.file || ''
          return <TruncatedLinkCell text={name} to={fileUrl} />
        },
        size: 300,
        meta: {
          headerTitle: 'Document Name',
          headerGroup: 'Document Info',
          isSticky: true,
          isFirstColumn: true,
          isEditable: false,
        },
      },
      {
        id: 'tags',
        header: 'Tags',
        accessorKey: 'tags',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          return <TaglistCell taglist={getValue() as any} tagType="documents" />
        },
        size: 200,
        meta: {
          headerGroup: 'Document Info',
          headerDisableOrdering: true,
        },
      },
      {
        id: 'file_extension',
        header: 'File Type',
        accessorKey: 'file_extension',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          const ext = getValue() as string
          if (!ext) return '-'
          if (linkVariant === 'plain') {
            return (
              <span
                className="inline-flex items-center rounded-2xl bg-neutral-100 px-2.5 py-0.5 text-sm font-medium uppercase leading-5 text-neutral-700"
                style={{ letterSpacing: '-0.2px' }}
              >
                {ext}
              </span>
            )
          }
          return (
            <span className="uppercase text-xs font-medium text-gray-600">
              {ext}
            </span>
          )
        },
        size: 100,
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'product_name',
        header: 'Product',
        accessorKey: 'product_name',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          const productName = getValue() as string
          return productName || '-'
        },
        size: 180,
        meta: {
          headerGroup: 'Associations',
        },
      },
      {
        id: 'variant_name',
        header: 'Variant',
        accessorKey: 'variant_name',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          const variant = getValue() as string
          return variant || '-'
        },
        size: 150,
        meta: {
          headerGroup: 'Associations',
        },
      },
      {
        id: 'company_name',
        header: 'Company',
        cell: ({ row }: CellContext<Document, unknown>) => {
          const companyName = row.original.contact_company_name
          const companyUrl = row.original.contact_company_url

          if (!companyName) return '-'

          // If no valid URL, just show the name as plain text
          if (!companyUrl) {
            return companyName
          }

          return (
            <TruncatedLinkCell text={companyName} to={companyUrl} target="" />
          )
        },
        size: 200,
        meta: {
          headerGroup: 'Associations',
        },
      },
      ...(enableBrandManagement
        ? [
            {
              id: 'brand_name',
              header: 'Brand',
              accessorKey: 'brand_name',
              cell: ({ getValue }: CellContext<Document, unknown>) => {
                return (getValue() as string) || '-'
              },
              size: 150,
              meta: {
                headerGroup: 'Associations',
              },
            },
          ]
        : []),
      {
        id: 'size',
        header: 'Size',
        accessorKey: 'size',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          const bytes = getValue() as number
          return formatBytes(bytes)
        },
        size: 100,
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'created',
        header: 'Upload Date',
        accessorKey: 'created',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          return formatDate(getValue() as string)
        },
        size: 120,
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'modified',
        header: 'Last Updated',
        accessorKey: 'modified',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          return formatDate(getValue() as string)
        },
        size: 120,
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'created_by_name',
        header: 'Uploaded By',
        accessorKey: 'created_by_name',
        cell: ({ getValue }: CellContext<Document, unknown>) => {
          const name = (getValue() as string | null) ?? ''
          if (!name) return <>—</>
          const initials = name
            .split(' ')
            .map((n) => n[0] ?? '')
            .join('')
            .toUpperCase()
            .slice(0, 2)
          return (
            <div className="flex items-center gap-2.5">
              <div
                className="flex shrink-0 items-center justify-center rounded-full text-xs font-medium leading-4"
                style={{
                  width: 24,
                  height: 24,
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  letterSpacing: '-0.2px',
                }}
              >
                {initials}
              </div>
              <span className="truncate">{name}</span>
            </div>
          )
        },
        size: 220,
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'version_number',
        header: 'Version',
        accessorKey: 'version_number',
        cell: ({ getValue, row }: CellContext<Document, unknown>) => {
          const version = getValue() as number
          const isLatest = row.original.is_latest
          return (
            <span className={cn(!isLatest && 'text-gray-400')}>
              v{version || 1}
              {!isLatest && ' (old)'}
            </span>
          )
        },
        size: 100,
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'actions',
        header:
          linkVariant === 'plain'
            ? () => <div className="w-full text-center">Actions</div>
            : 'Actions',
        size: linkVariant === 'plain' ? 70 : 80,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          rightAlign: linkVariant !== 'plain',
          isLastColumn: true,
        },
        cell: ({ row }: CellContext<Document, unknown>) => {
          const doc = row.original

          const actions = [
            {
              label: 'View',
              icon: PiEye,
              onClick: () => window.open(doc.file, '_blank'),
            },
            {
              label: 'Download',
              icon: PiDownloadSimple,
              onClick: async () => {
                try {
                  // Fetch the file as a blob to force download
                  const response = await fetch(doc.file || '')
                  const blob = await response.blob()
                  const url = window.URL.createObjectURL(blob)
                  const link = document.createElement('a')
                  link.href = url
                  link.download = doc.name
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                  window.URL.revokeObjectURL(url)
                } catch (error) {
                  toast.error('Failed to download file')
                }
              },
            },
            {
              label: 'Replace',
              icon: PiSwap,
              disabled: doc.is_pim_asset,
              disabledReason: doc.is_pim_asset
                ? 'This document is managed by a PIM system and cannot be replaced'
                : undefined,
              onClick: () => {
                setSelectedDocument(doc)
                setReplaceModalOpen(true)
              },
            },
            {
              label: 'Edit',
              icon: PiPencilSimple,
              onClick: () => {
                setSelectedDocument(doc)
                setEditModalOpen(true)
              },
            },
            {
              label: 'Delete',
              icon: PiTrash,
              variant: 'destructive' as const,
              disabled: doc.is_pim_asset,
              disabledReason: doc.is_pim_asset
                ? 'This document is managed by a PIM system and cannot be deleted'
                : undefined,
              onClick: () => {
                setSelectedDocument(doc)
                setDeleteModalOpen(true)
              },
            },
          ]

          return (
            <ActionsDropdown
              actions={actions}
              className={
                linkVariant === 'plain' ? 'justify-center' : 'justify-end'
              }
            />
          )
        },
      },
    ],
    []
  )

  // Success handler for modals
  const handleSuccess = () => {
    toast.success('Document updated successfully')
    // Invalidate all document table queries (both main page and modals)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.startsWith('DOCUMENTS_') ||
            firstKey.startsWith('documents-modal-'))
        )
      },
    })
    // Invalidate activity tables and company activity (to show document actions in activity feed)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.includes('activity-table') ||
            firstKey === 'companyActivity')
        )
      },
    })
    // Invalidate storage quota query (in case associations changed)
    queryClient.invalidateQueries({
      queryKey: ['documents', 'quota'],
    })
  }

  const handleDeleteSuccess = () => {
    toast.success('Document deleted successfully')
    // Invalidate all document table queries (both main page and modals)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.startsWith('DOCUMENTS_') ||
            firstKey.startsWith('documents-modal-'))
        )
      },
    })
    // Invalidate activity tables and company activity (to show document actions in activity feed)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.includes('activity-table') ||
            firstKey === 'companyActivity')
        )
      },
    })
    // Invalidate storage quota query (storage freed)
    queryClient.invalidateQueries({
      queryKey: ['documents', 'quota'],
    })
    // Invalidate company/door data to update document count
    if (forChainProxyId) {
      queryClient.invalidateQueries({
        queryKey: ['company', forChainProxyId],
      })
    }
    if (forDoorProxyId) {
      queryClient.invalidateQueries({
        queryKey: ['door_proxy', forDoorProxyId],
      })
    }
  }

  const bulkActionItems = [
    {
      label: 'Download Documents',
      icon: <PiDownloadSimple size={20} />,
      callback: handleBulkDownloadDocuments,
      disabled:
        selectedDocumentIds.length > 50 ||
        (downloadState.status !== 'idle' &&
          downloadState.status !== 'completed' &&
          downloadState.status !== 'failed'),
      disabledTooltipText:
        selectedDocumentIds.length > 50
          ? 'Maximum 50 documents can be selected for download'
          : 'A download is already in progress',
    },
    ...(enableBrandManagement
      ? [
          {
            label: 'Assign Brand',
            icon: <PiTag size={20} />,
            subContent: (
              <DocumentLinkBrandCombobox
                selectedDocumentIds={selectedDocumentIds}
                onSuccess={() => clearSelectedRows()}
                buttonLess
              />
            ),
          },
        ]
      : []),
  ]

  const tableHeader = (
    <S.SearchContainer>
      <div className="flex w-full gap-2 items-center">
        <TableSearch
          className={'flex-1'}
          filterStore={documentsFilterStore}
          searchPlaceholder={searchPlaceholder}
          tableKey={tableKey}
        />
        {!hideFilterButton && (
          <DocumentsFilterset filterIdentifier={filterIdentifier} />
        )}
        {!hideColumnSelector && <ColumnSelectorRecipient tableKey={tableKey} />}
      </div>
      <div className={cn('flex flex-row justify-between gap-4')}>
        {linkVariant !== 'plain' && (
          <TableButtonAction
            items={bulkActionItems}
            selectedRowsCount={totalSelectedRows}
            entityType="documents"
          />
        )}
        {!hideFilterButton && (
          <FilterChips
            identifier={filterIdentifier}
            store={documentsFilterStore}
            clearAllButton
            showActive
          />
        )}
      </div>
    </S.SearchContainer>
  )

  return (
    <>
      <DataTableContainer>
        <DataTable
          tableHeader={tableHeader}
          selectHeaderActions={
            linkVariant === 'plain' ? (
              <TableButtonAction
                items={bulkActionItems}
                selectedRowsCount={totalSelectedRows}
                entityType="documents"
              />
            ) : undefined
          }
          loading={isFetching}
          tableKey={tableKey}
          data={data?.results ?? []}
          columns={columns as any}
          sortableFields={[
            'name',
            'product_name',
            'company_name',
            ...(enableBrandManagement ? ['brand_name'] : []),
            'size',
            'created',
            'modified',
            'created_by_name',
          ]}
          virtualizeRows
          enableRowSelection
          selectAllText="Select Documents"
          isPaginationEnabled
          stickyLastColumn={linkVariant === 'plain'}
          preventLastColumnSticky={linkVariant !== 'plain'}
          paginationOptions={{
            totalRows: data?.count ?? 0,
            pageSize: PAGE_SIZE,
            setPage,
            page,
            isPaginationLoading: isFetching,
          }}
          emptyTableText={{
            title: 'No documents found',
            subtitle: 'Try adjusting your filters or upload a new document',
          }}
        />
      </DataTableContainer>

      {/* Edit Modal */}
      {selectedDocument && (
        <EditDocumentModal
          document={selectedDocument}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false)
            setSelectedDocument(null)
          }}
          onSuccess={handleSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {selectedDocument && (
        <DeleteDocumentConfirmation
          document={selectedDocument}
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false)
            setSelectedDocument(null)
          }}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {/* Replace Modal */}
      {selectedDocument && (
        <ReplaceDocumentModal
          document={selectedDocument}
          isOpen={replaceModalOpen}
          onClose={() => {
            setReplaceModalOpen(false)
            setSelectedDocument(null)
          }}
          onSuccess={handleSuccess}
        />
      )}
    </>
  )
}

export function DocumentsTable(
  props: DocumentsTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  const defaultVisibility = defaultColumnVisibility || {
    name: true,
    tags: true,
    file_extension: true,
    product_name: true,
    variant_name: false,
    company_name: true,
    brand_name: false,
    size: true,
    created: true,
    modified: false,
    created_by_name: true,
    version_number: false,
    actions: true,
  }

  const initialColumnOrder = [
    'name',
    'tags',
    'file_extension',
    'product_name',
    'variant_name',
    'company_name',
    'brand_name',
    'size',
    'created',
    'modified',
    'created_by_name',
    'version_number',
    'actions',
  ]

  const preferredGroupOrder = [
    'Document Info',
    'Associations',
    'Metadata',
    'Actions',
  ]

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      savedPresetsIdentifier={props.filterIdentifier}
      defaultColumnVisibility={defaultVisibility}
      initialColumnOrder={initialColumnOrder}
      preferredGroupOrder={preferredGroupOrder}
    >
      <DataTableProvider tableKey={tableKey}>
        <DocumentsTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
